<template>
  <PolicyChangeForm
    title="Document Management"
    :steps="formSteps"
    :current-step="currentStep"
    :usingPortalForm="true"
    @next="handleNext"
    @back="handleBack"
    @submit="handleSubmit"
  >
    <!-- Step 1: Select Action -->
    <template #step1>
      <div class="p-4">
        <h3 class="text-xl font-semibold mb-4 text-center">
          What would you like to do?
        </h3>
        
        <div class="flex flex-col gap-4 md:flex-row md:justify-center">
          <button
            @click="setAction('download')"
            :class="[
              'p-4 rounded-lg shadow-md transition-all duration-300 flex flex-col items-center justify-center space-y-2',
              action === 'download' 
                ? 'bg-blue-100 border-2 border-blue-500' 
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            ]"
            style="min-width: 200px; min-height: 150px"
          >
            <Icon icon="mdi:download-circle-outline" class="text-4xl text-blue-600" />
            <span class="text-lg font-medium">Download Forms</span>
            <span class="text-sm text-gray-600">Get standard insurance forms</span>
          </button>
          
          <button
            @click="setAction('upload')"
            :class="[
              'p-4 rounded-lg shadow-md transition-all duration-300 flex flex-col items-center justify-center space-y-2',
              action === 'upload' 
                ? 'bg-blue-100 border-2 border-blue-500' 
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            ]"
            style="min-width: 200px; min-height: 150px"
          >
            <Icon icon="mdi:upload-circle-outline" class="text-4xl text-blue-600" />
            <span class="text-lg font-medium">Upload Documents</span>
            <span class="text-sm text-gray-600">Submit documents to us</span>
          </button>
        </div>
        
        <div v-if="actionError" class="mt-4 text-red-500 text-center">
          {{ actionError }}
        </div>
      </div>
    </template>
    
    <!-- Step 2: Download or Upload -->
    <template #step2>
      <!-- Document Download View -->
      <div v-if="action === 'download'" class="p-4">
        <DocumentDownload
          title="Available Documents"
          :documents="availableDocuments"
        />
        
        <div class="mt-8 text-center">
          <p class="text-gray-600 text-sm">
            If you need a specific document that's not listed here, please contact our customer service.
          </p>
        </div>
      </div>
      
      <!-- Document Upload View - Policy Information -->
      <div v-else-if="action === 'upload'" class="p-4">
        <h3 class="text-xl font-semibold mb-4 text-center">
          Policy Information
        </h3>
        
        <div class="space-y-4 max-w-lg mx-auto">
          <div>
            <label class="block text-gray-700 text-sm font-bold mb-2" for="first-name">
              First Name
            </label>
            <input
              v-model="formValues.firstName"
              type="text"
              id="first-name"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <div v-if="errors.firstName" class="text-red-500 text-xs mt-1">
              {{ errors.firstName }}
            </div>
          </div>
          
          <div>
            <label class="block text-gray-700 text-sm font-bold mb-2" for="last-name">
              Last Name
            </label>
            <input
              v-model="formValues.lastName"
              id="last-name"
              type="text"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <div v-if="errors.lastName" class="text-red-500 text-xs mt-1">
              {{ errors.lastName }}
            </div>
          </div>
          
          <div>
            <label class="block text-gray-700 text-sm font-bold mb-2" for="policy-number">
              Policy Number
            </label>
            <input
              v-model="formValues.policyNumber"
              id="policy-number"
              type="text"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <div v-if="errors.policyNumber" class="text-red-500 text-xs mt-1">
              {{ errors.policyNumber }}
            </div>
          </div>
          
          <div>
            <label for="phone-number" class="block text-gray-700 text-sm font-bold mb-2">
              Phone Number
            </label>
            <input
              v-model="formValues.phoneNumber"
              id="phone-number"
              type="tel"
              placeholder="(XXX) XXX-XXXX"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <div v-if="errors.phoneNumber" class="text-red-500 text-xs mt-1">
              {{ errors.phoneNumber }}
            </div>
          </div>
          
          <div>
            <label for="email" class="block text-gray-700 text-sm font-bold mb-2">
              Email
            </label>
            <input
              v-model="formValues.email"
              id="email"
              type="email"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <div v-if="errors.email" class="text-red-500 text-xs mt-1">
              {{ errors.email }}
            </div>
          </div>
        </div>
      </div>
    </template>
    
    <!-- Step 3: Upload Document -->
    <template #step3>
      <div class="p-4">
        <h3 class="text-xl font-semibold mb-4 text-center">
          Upload Document
        </h3>
        
        <div class="max-w-lg mx-auto">
          <!-- File Upload Area -->
          <div
            class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors duration-300"
            @click="triggerFileInput"
            @dragover.prevent="dragover = true"
            @dragleave.prevent="dragover = false"
            @drop.prevent="handleFileDrop"
            :class="{ 'bg-blue-50 border-blue-300': dragover }"
          >
            <input
              type="file"
              ref="fileInput"
              class="hidden"
              @change="handleFileChange"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            
            <div v-if="!selectedFile">
              <Icon icon="heroicons:document-arrow-up" class="text-5xl text-blue-500 mx-auto" />
              <p class="mt-2 text-sm text-gray-600">
                Drag and drop a file here, or click to select a file
              </p>
              <p class="mt-1 text-xs text-gray-500">
                Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG
              </p>
            </div>
            
            <div v-else class="text-left">
              <div class="flex items-center">
                <Icon icon="heroicons:document" class="text-2xl text-blue-500 mr-2" />
                <span class="text-sm font-medium truncate">{{ selectedFile.name }}</span>
              </div>
              <div class="mt-2 flex items-center justify-between">
                <span class="text-xs text-gray-500">
                  {{ formatFileSize(selectedFile.size) }}
                </span>
                <button
                  @click.stop="removeFile"
                  class="text-red-500 hover:text-red-700 text-xs"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          
          <div v-if="errors.file" class="text-red-500 text-xs mt-1">
            {{ errors.file }}
          </div>
          
          <!-- Upload Progress -->
          <div v-if="documentService.uploadProgress > 0 && documentService.uploadProgress < 100" class="mt-4">
            <div class="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full bg-blue-500 transition-all duration-300"
                :style="{ width: `${documentService.uploadProgress}%` }"
              ></div>
            </div>
            <p class="text-xs text-gray-600 text-center mt-1">
              Uploading... {{ documentService.uploadProgress }}%
            </p>
          </div>
          
          <!-- Message -->
          <div class="mt-6">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="message">
              Message
            </label>
            <textarea
              v-model="formValues.message"
              id="message"
              rows="4"
              class="shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
              placeholder="Please add any relevant details about your document..."
            ></textarea>
            <div v-if="errors.message" class="text-red-500 text-xs mt-1">
              {{ errors.message }}
            </div>
          </div>
        </div>
      </div>
    </template>
    
    <!-- Success View -->
    <template #success>
      <div class="p-8 text-center">
        <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Icon icon="heroicons:check" class="text-3xl text-green-500" />
        </div>
        
        <h3 class="text-xl font-semibold mb-2">Document Uploaded Successfully</h3>
        
        <p class="text-gray-600 mb-6">
          Thank you for your submission. We'll review your document and contact you if needed.
        </p>
        
        <p class="text-gray-600 text-sm mb-4">
          A confirmation has been sent to: <span class="font-medium">{{ formValues.email }}</span>
        </p>
        
        <button
          @click="resetForm"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline"
        >
          Done
        </button>
      </div>
    </template>
  </PolicyChangeForm>
</template>

<script setup>
import { ref, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import PolicyChangeForm from '@/components/customerservice/policy-management/base/PolicyChangeForm.vue';
import DocumentDownload from './DocumentDownload.vue';
import { useDocumentService } from '@/services/DocumentService';

// Setup router and document service
const router = useRouter();
const documentService = useDocumentService();

// Component state
const currentStep = ref(1);
const action = ref(null);
const actionError = ref(null);
const dragover = ref(false);
const fileInput = ref(null);
const selectedFile = ref(null);
const errors = reactive({});

// Form values
const formValues = reactive({
  firstName: '',
  lastName: '',
  policyNumber: '',
  phoneNumber: '',
  email: '',
  message: '',
});

// Available documents - from service
const availableDocuments = computed(() => {
  return documentService.getAvailableDocuments();
});

// Form steps based on selected action
const formSteps = computed(() => {
  if (action.value === 'download') {
    return [
      { label: 'Select Action', number: 1 },
      { label: 'Download Documents', number: 2 },
    ];
  } else if (action.value === 'upload') {
    return [
      { label: 'Select Action', number: 1 },
      { label: 'Policy Information', number: 2 },
      { label: 'Upload Document', number: 3 },
    ];
  } else {
    return [
      { label: 'Select Action', number: 1 },
    ];
  }
});

// Set the action (download or upload)
const setAction = (selectedAction) => {
  action.value = selectedAction;
  actionError.value = null;
};

// Handle next button click
const handleNext = () => {
  actionError.value = null;
  // Clear errors object without reassigning it
  Object.keys(errors).forEach(key => delete errors[key]);
  
  // Validate based on current step
  if (currentStep.value === 1) {
    if (!action.value) {
      actionError.value = 'Please select an option to continue';
      return;
    }
    
    currentStep.value++;
    return;
  }
  
  if (currentStep.value === 2 && action.value === 'upload') {
    // Validate form fields
    if (!validatePolicyForm()) {
      return;
    }
    
    currentStep.value++;
    return;
  }
};

// Handle back button click
const handleBack = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  } else {
    router.push({ name: 'customerServicePage' });
  }
};

// Validate policy form
const validatePolicyForm = () => {
  const newErrors = {};
  
  if (!formValues.firstName.trim()) {
    newErrors.firstName = 'First name is required';
  }
  
  if (!formValues.lastName.trim()) {
    newErrors.lastName = 'Last name is required';
  }
  
  if (!formValues.policyNumber.trim()) {
    newErrors.policyNumber = 'Policy number is required';
  }
  
  // Validate phone format
  const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$|^\d{10}$/;
  if (!formValues.phoneNumber.trim()) {
    newErrors.phoneNumber = 'Phone number is required';
  } else if (!phoneRegex.test(formValues.phoneNumber.trim())) {
    newErrors.phoneNumber = 'Please enter a valid phone number';
  }
  
  // Validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formValues.email.trim()) {
    newErrors.email = 'Email is required';
  } else if (!emailRegex.test(formValues.email.trim())) {
    newErrors.email = 'Please enter a valid email address';
  }
  
  // Update errors
  Object.assign(errors, newErrors);
  
  return Object.keys(newErrors).length === 0;
};

// Validate upload form
const validateUploadForm = () => {
  const newErrors = {};
  
  if (!selectedFile.value) {
    newErrors.file = 'Please select a file to upload';
  }
  
  if (!formValues.message.trim()) {
    newErrors.message = 'Please provide a message';
  }
  
  // Update errors
  Object.assign(errors, newErrors);
  
  return Object.keys(newErrors).length === 0;
};

// Trigger file input click
const triggerFileInput = () => {
  fileInput.value.click();
};

// Handle file change
const handleFileChange = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

// Handle file drop
const handleFileDrop = (event) => {
  dragover.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

// Remove selected file
const removeFile = (event) => {
  event.stopPropagation();
  selectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Handle form submission
const handleSubmit = async () => {
  if (!validateUploadForm()) {
    return;
  }
  
  try {
    await documentService.submitDocument(selectedFile.value, {
      ...formValues,
      useEmailJS: true // Use EmailJS for now to maintain compatibility
    });
    
    // Move to success state
    currentStep.value = 'success';
  } catch (error) {
    console.error('Error submitting document:', error);
  }
};

// Reset form
const resetForm = () => {
  // Reset form state
  currentStep.value = 1;
  action.value = null;
  selectedFile.value = null;
  
  // Reset form values
  Object.keys(formValues).forEach(key => {
    formValues[key] = '';
  });
  
  // Clear errors
  Object.keys(errors).forEach(key => {
    delete errors[key];
  });
  
  // Go back to customer service page
  router.push({ name: 'customerServicePage' });
};
</script>

<style scoped>
/* Component-specific styles go here */
</style> 